import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import './ImageHoverEffect.css';

const ImageHoverEffect = ({ src, alt, className, fallback, onClick }) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const materialRef = useRef(null);
  const meshRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mousePositionRef = useRef({ x: 0.5, y: 0.5 });
  const targetMousePositionRef = useRef({ x: 0.5, y: 0.5 });
  const progressRef = useRef(0);
  const timeRef = useRef(0);
  const textureRef = useRef(null);
  const mouseIntervalRef = useRef(null);
  
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [naturalAspectRatio, setNaturalAspectRatio] = useState(null);
  // Determine desktop based on window.innerWidth
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  // Add a state to track if the component should show pointer cursor
  const [showPointer, setShowPointer] = useState(false);

  // Update isDesktop on window resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Initialize dimensions and observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;
    const updateDimensions = () => {
      if (!containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      setDimensions({ width, height });
    };
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);
    return () => {
      if (containerRef.current) resizeObserver.unobserve(containerRef.current);
      resizeObserver.disconnect();
    };
  }, []);
  
  // Preload image to get natural dimensions
  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setNaturalAspectRatio(img.naturalWidth / img.naturalHeight);
    };
    return () => { img.onload = null; };
  }, [src]);
  
  // Initialize WebGL only for desktop viewports
  useEffect(() => {
    if (
      !isDesktop ||
      !containerRef.current ||
      !dimensions.width ||
      !dimensions.height ||
      !naturalAspectRatio
    )
      return;
    
    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    // Calculate dimensions preserving aspect ratio
    let planeWidth, planeHeight;
    if (dimensions.width / dimensions.height > naturalAspectRatio) {
      planeHeight = dimensions.height;
      planeWidth = planeHeight * naturalAspectRatio;
    } else {
      planeWidth = dimensions.width;
      planeHeight = planeWidth / naturalAspectRatio;
    }
    
    // Create camera
    const camera = new THREE.OrthographicCamera(
      -dimensions.width / 2, 
      dimensions.width / 2, 
      dimensions.height / 2, 
      -dimensions.height / 2, 
      0.1, 
      1000
    );
    camera.position.z = 1;
    cameraRef.current = camera;
    
    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true
    });
    renderer.setSize(dimensions.width, dimensions.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    
    // Load texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.crossOrigin = 'anonymous';
    
    // Create placeholder material
    const placeholderMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0
    });
    
    // Create mesh with placeholder material
    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, 1, 1);
    const mesh = new THREE.Mesh(geometry, placeholderMaterial);
    scene.add(mesh);
    meshRef.current = mesh;
    
    // Initial render
    renderer.render(scene, camera);
    
    // Load actual texture and update material
    textureLoader.load(
      src,
      (texture) => {
        textureRef.current = texture;
        const material = new THREE.ShaderMaterial({
          uniforms: {
            uTexture: { value: texture },
            uTime: { value: 0 },
            uProgress: { value: 0 },
            uMouse: { value: new THREE.Vector2(0.5, 0.5) },
            uResolution: { value: new THREE.Vector2(dimensions.width, dimensions.height) },
            uImageResolution: { value: new THREE.Vector2(texture.image.width, texture.image.height) }
          },
          vertexShader: `
            varying vec2 vUv;
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform sampler2D uTexture;
            uniform float uTime;
            uniform float uProgress;
            uniform vec2 uMouse;
            uniform vec2 uResolution;
            uniform vec2 uImageResolution;
            varying vec2 vUv;
            void main() {
              vec2 distortedUv = vUv;
              
              // Only apply distortion if progress is above a small threshold
              if (uProgress > 0.01) {
                // Reduce distortion near edges with edge protection
                float edgeProtection = 0.8 * (1.0 - smoothstep(0.4, 0.5, abs(vUv.x - 0.5))) * (1.0 - smoothstep(0.4, 0.5, abs(vUv.y - 0.5)));
                float distortionStrength = 0.05 * uProgress * edgeProtection;
                
                vec2 offset = vec2(
                  sin(vUv.y * 8.0 + uTime) * distortionStrength,
                  cos(vUv.x * 8.0 + uTime) * distortionStrength
                );
                
                vec2 mouseDirection = uMouse - vec2(0.5);
                float mouseDistance = length(vUv - uMouse);
                float mouseFalloff = smoothstep(0.5, 0.0, mouseDistance);
                
                // Also reduce mouse-based distortion near edges
                vec2 mouseOffset = mouseDirection * 0.08 * uProgress * mouseFalloff * edgeProtection;
                
                distortedUv = vUv + offset + mouseOffset;
                distortedUv = clamp(distortedUv, 0.01, 0.99);
              }
              
              vec4 color = texture2D(uTexture, distortedUv);
              
              // Only apply brightness increase if progress is above threshold
              if (uProgress > 0.01) {
                float brightness = 1.0 + 0.2 * uProgress;
                color.rgb *= brightness;
              }
              
              gl_FragColor = color;
            }
          `,
          transparent: true
        });
        
        if (meshRef.current) {
          meshRef.current.material = material;
          materialRef.current = material;
          progressRef.current = 0;
          setIsLoaded(true);
          renderer.render(scene, camera);
        }
      },
      undefined,
      (error) => console.error('Error loading texture:', error)
    );
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      if (mouseIntervalRef.current) clearInterval(mouseIntervalRef.current);
      if (meshRef.current) {
        if (meshRef.current.geometry) meshRef.current.geometry.dispose();
        if (meshRef.current.material) {
          if (meshRef.current.material.map) meshRef.current.material.map.dispose();
          meshRef.current.material.dispose();
        }
      }
      if (textureRef.current) textureRef.current.dispose();
      if (scene) scene.clear();
      if (renderer && containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
        renderer.dispose();
        renderer.forceContextLoss();
      }
      materialRef.current = null;
      meshRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      textureRef.current = null;
    };
  }, [dimensions.width, dimensions.height, src, naturalAspectRatio, isDesktop]);
  
  // Animation loop for WebGL effect on desktop with performance tweaks
  useEffect(() => {
    if (
      !isDesktop ||
      !materialRef.current ||
      !rendererRef.current ||
      !sceneRef.current ||
      !cameraRef.current
    )
      return;
    
    let lastTime = 0;
    const idleFPS = 30;
    const activeFPS = 60;
    let targetFPS = isHovered ? activeFPS : idleFPS;
    let frameInterval = 1000 / targetFPS;
    
    const animate = (time) => {
      animationFrameRef.current = requestAnimationFrame(animate);
      
      // Pause heavy updates if document is hidden
      if (document.visibilityState !== 'visible') return;
      
      targetFPS = isHovered ? activeFPS : idleFPS;
      frameInterval = 1000 / targetFPS;
      const delta = time - lastTime;
      if (delta < frameInterval) return;
      lastTime = time - (delta % frameInterval);
      
      if (isHovered || progressRef.current > 0) {
        timeRef.current += 0.01;
      }
      
      const targetProgress = isHovered ? 1 : 0;
      // Improved easing with cubic bezier-like behavior
      const easeInOutCubic = isHovered 
        ? 0.15  // Faster easing in
        : 0.08; // Slower easing out
      progressRef.current += (targetProgress - progressRef.current) * easeInOutCubic;
      
      // Improved mouse tracking with variable easing
      const mouseEasing = isHovered ? 0.15 : 0.08;
      mousePositionRef.current.x += (targetMousePositionRef.current.x - mousePositionRef.current.x) * mouseEasing;
      mousePositionRef.current.y += (targetMousePositionRef.current.y - mousePositionRef.current.y) * mouseEasing;
      
      if (materialRef.current) {
        materialRef.current.uniforms.uTime.value = timeRef.current;
        materialRef.current.uniforms.uProgress.value = progressRef.current;
        materialRef.current.uniforms.uMouse.value.set(
          mousePositionRef.current.x,
          mousePositionRef.current.y
        );
      }
      
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      
      if (!isHovered && progressRef.current < 0.01) {
        progressRef.current = 0;
        setTimeout(() => {
          if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
        }, 500);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [isHovered, isDesktop]);
  
  // Update renderer and mesh on resize (only when WebGL is active)
  useEffect(() => {
    if (
      !isDesktop ||
      !cameraRef.current ||
      !rendererRef.current ||
      !meshRef.current ||
      !naturalAspectRatio
    )
      return;
    
    cameraRef.current.left = -dimensions.width / 2;
    cameraRef.current.right = dimensions.width / 2;
    cameraRef.current.top = dimensions.height / 2;
    cameraRef.current.bottom = -dimensions.height / 2;
    cameraRef.current.updateProjectionMatrix();
    
    rendererRef.current.setSize(dimensions.width, dimensions.height);
    
    let planeWidth, planeHeight;
    if (dimensions.width / dimensions.height > naturalAspectRatio) {
      planeHeight = dimensions.height;
      planeWidth = planeHeight * naturalAspectRatio;
    } else {
      planeWidth = dimensions.width;
      planeHeight = planeWidth / naturalAspectRatio;
    }
    
    if (meshRef.current.geometry) {
      meshRef.current.geometry.dispose();
      meshRef.current.geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, 1, 1);
    }
    
    if (materialRef.current) {
      materialRef.current.uniforms.uResolution.value.set(dimensions.width, dimensions.height);
    }
  }, [dimensions, naturalAspectRatio, isDesktop]);
  
  // Handle mouse move (desktop only)
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    targetMousePositionRef.current = { x, y };
  };
  
  // Simulate subtle mouse movement on hover (desktop only)
  useEffect(() => {
    if (!isDesktop) return;
    if (mouseIntervalRef.current) {
      clearInterval(mouseIntervalRef.current);
      mouseIntervalRef.current = null;
    }
    if (isHovered && isLoaded) {
      mouseIntervalRef.current = setInterval(() => {
        const x = 0.5 + Math.sin(Date.now() * 0.0005) * 0.1;
        const y = 0.5 + Math.cos(Date.now() * 0.0005) * 0.1;
        targetMousePositionRef.current = { x, y };
      }, 50);
    }
    return () => {
      if (mouseIntervalRef.current) clearInterval(mouseIntervalRef.current);
    };
  }, [isHovered, isLoaded, isDesktop]);
  
  // Handle mouse enter (desktop only)
  const handleMouseEnter = () => {
    setIsHovered(true);
    // Show pointer cursor on hover
    setShowPointer(true);
    // Add special class to document body that will be used to hide animated cursor
    document.body.classList.add('hover-effect-active');
  };
  
  // Handle mouse leave (desktop only)
  const handleMouseLeave = () => {
    setIsHovered(false);
    // Reset pointer cursor
    setShowPointer(false);
    // Remove special class from document body
    document.body.classList.remove('hover-effect-active');
  };
  
  // Use the provided fallback image (if any) or default to src
  const fallbackImage = fallback || src;
  
  return (
    <div 
      ref={containerRef}
      className={`image-hover-container ${className} ${showPointer ? 'portfolio-image-hover' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={isDesktop ? handleMouseMove : undefined}
      onClick={onClick}
      style={{ 
        width: '100%', 
        height: 'auto', 
        position: 'relative',
        cursor: showPointer ? 'pointer' : 'none' 
      }}
    >
      {/* Loader overlay while WebGL is initializing */}
      {!isLoaded && (
        <div className="image-hover-loader">
          <div className="spinner"></div>
        </div>
      )}
      {/* Fallback image */}
      <img 
        src={isLoaded && isDesktop ? src : fallbackImage}
        alt={alt}
        className={`hover-image ${className}`}
        style={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          opacity: isLoaded && isDesktop ? 0 : 1,
          position: isLoaded && isDesktop ? 'absolute' : 'relative',
          pointerEvents: 'none',
          transition: 'opacity 0.5s ease',
          cursor: showPointer ? 'pointer' : 'none'
        }}
      />
    </div>
  );
};

export default ImageHoverEffect;
